/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

/* eslint-disable complexity */

import { Account } from '@carbon/react/icons';
import { Modal, TextInput } from 'carbon-components-react';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { selectContactCenterProvider } from '../../../store/phone/selectors';
import { ControlledCheckbox } from '../../common/ControlledCheckbox';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { TextAreaWithCharacterCount } from '../../common/TextAreaWithCharacterCount';
import { ServiceDesk } from '../../service-desk/messages';
import { LiveAgent as liveAgentMessages } from '../connections-tab/messages';
import { links } from '../links';
import { FIELDS } from '../schema';
import { AdvancedOptions as messages } from './messages';
import { ServiceOrchestration } from './ServiceOrchestration';
import { SipAuthentication } from './SipAuthentication';

import '../Phone.scss';

interface AdvancedOptionsProps {
  tabname?: string;
  showSOE: boolean;
  readOnly?: boolean;
}
export const AdvancedOptions = ({
  showSOE,
  readOnly
}: AdvancedOptionsProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { registerInputField, updateFieldValue } = useFormHelpers(formMethods);

  const [displayModal, setDisplayModal] = useState(false);

  const contactCenterProvider = useAppSelector(selectContactCenterProvider);

  const onClose = () => {
    updateFieldValue(FIELDS.ERRORS.TRANSFER.DISCONNECT_CALL, true);
    setDisplayModal(false);
  };

  const onSubmit = () => {
    updateFieldValue(FIELDS.ERRORS.TRANSFER.DISCONNECT_CALL, false);
    setDisplayModal(false);
  };

  const textAreaWithCharacterCountData = [
    {
      field: FIELDS.ERRORS.CALL.REPLY_MESSAGE,
      labelText: liveAgentMessages.callFailureLabel,
      placeHolder: liveAgentMessages.callFailureDefault
    },
    {
      field: FIELDS.ERRORS.TRANSFER.REPLY_MESSAGE,
      labelText: liveAgentMessages.transferFailureLabel,
      placeHolder: liveAgentMessages.transferFailureDefault
    }
  ];

  return (
    <TabContent
      ImageComponent={() => <Account size={32} />}
      title={<FormattedMessage {...messages.title} />}>
      <div className="Phone__body-content TabContent__body-content">
        <SipAuthentication readOnly={readOnly} />
        {showSOE && <ServiceOrchestration readOnly={readOnly} />}
        <div className="TabContent__instructions-no-spacer">
          <h4>
            <FormattedMessage {...liveAgentMessages.subtitle} />
          </h4>
          <div className="TabContent__subtitle">
            <FormattedMessage {...messages.callFailureDescription} />
          </div>
          {textAreaWithCharacterCountData.map((inputData, index) => (
            <div
              className="TabContent__field-container"
              key={`area-input-${index}`}>
              <TextAreaWithCharacterCount
                field={inputData.field}
                readOnly={readOnly}
                labelText={inputData.labelText}
                placeholder={t(inputData.placeHolder)}
                maximumFieldLength={
                  ServiceDesk.maximumFieldLength.defaultMessage
                }
              />
            </div>
          ))}
          {!contactCenterProvider && (
            <div className="TabContent__field-container">
              <TextInput
                {...registerInputField({
                  field: FIELDS.TRANSFER_FAILURE_AGENT,
                  placeholderText: liveAgentMessages.sipTargetPlaceholder,
                  labelText: liveAgentMessages.sipTargetLabel,
                  readOnly
                })}
                autoComplete="url"
              />
            </div>
          )}
          <div className="TabContent__spacer16" />
          <ControlledCheckbox
            readOnly={readOnly}
            field={FIELDS.ERRORS.TRANSFER.DISCONNECT_CALL}
            labelText={t(liveAgentMessages.disconnectOnFailure)}
            onChange={e => {
              setDisplayModal(!e);
            }}
          />
          <Modal
            size="xs"
            id="advanceOptions-modal"
            primaryButtonText={t(messages.transferFailurePrimary)}
            secondaryButtonText={t(messages.transferFailureSecondary)}
            modalHeading={t(messages.transferFailureModalHeading)}
            open={displayModal}
            onRequestClose={onClose}
            onRequestSubmit={onSubmit}>
            <FormattedMessageWithLink
              url={links.learnMoreAdvancedOptionsModalLink}
              {...messages.transferFailureModalBody}
            />
          </Modal>
          {useIsNewIA() && (
            <div className="TabContent__instructions-no-spacer">
              <h4>
                <FormattedMessage {...messages.customExtension} />
              </h4>
              <div className="TabContent__spacer16" />
              <ControlledCheckbox
                readOnly={readOnly}
                field={FIELDS.ADVANCED_CUSTOM_EXTN_ASYNC_CALLOUT}
                labelText={t(messages.customExtensionAsync)}
              />
            </div>
          )}
          <div className="TabContent__spacer32" />
          <div className="Phone__body-content-width TabContent__subtitle TabContent__link-larger-text TabContent__subtitle-no-margin">
            <FormattedMessageWithLink
              url={
                useIsNewIA()
                  ? links.learnMoreAdvancedOptionsNewIA
                  : links.learnMoreAdvancedOptionsLink
              }
              {...messages.description}
            />
          </div>
          <div className="Phone__body-content-width TabContent__subtitle TabContent__link-larger-text">
            <FormattedMessageWithLink
              url={links.customDeployment}
              {...messages.customDeploymentDescription}
            />
          </div>
        </div>
      </div>
    </TabContent>
  );
};
